import React, { useEffect, useState, useMemo, useRef } from "react";
import tradeAnalyzeBg from "../../assets/imgs/trade-analyze-bg.svg";
import ChatbotImg from "../../assets/imgs/ai-chat-logo.svg";
import { hapticsImpactLight, mapWithFallback } from "../../lib/utils";
import { teamColors } from "../../config/constants/teamColors";
import NullPlayerImage from "../../assets/imgs/null-player-img.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { tradesAnalyze } from "../../Controllers/TradeAnalyzer/TradeAnalyzer";
import TradeAnalyzeLoading from "./Componet/TradeAnalyzeLoading";
import { TextGenerateEffect } from "../ui/text-generate-effect";
import { TextShimmer } from "../ui/text-shimmer";
import { useLeagueStore } from "../../zustandStore/leagueStore";
import { useMixpanel } from "../../Context/MixpanelContext/MixpanelProvider";

export default function TradeAnalyzeReportScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const Mixpanel = useMixpanel();
  // Get the navigation state data
  const { tradingAwayPlayers, tradingForPlayers } = location.state || {};

  // State for API response
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const hasCalledApi = useRef(false);
  const { LeagueType } = useLeagueStore();

  const updatedTradingAwayPlayers = useMemo(() => {
    return (
      tradingAwayPlayers?.map((player: any) => ({
        ...player,
        season:
          typeof player.season === "number"
            ? player.season.toString()
            : player.season,
      })) || []
    );
  }, [tradingAwayPlayers]);

  const updatedTradingForPlayers = useMemo(() => {
    return (
      tradingForPlayers?.map((player: any) => ({
        ...player,
        season:
          typeof player.season === "number"
            ? player.season.toString()
            : player.season,
      })) || []
    );
  }, [tradingForPlayers]);

  const tradesAnalyzeBody = {
    league_settings: {
      setting1: "value1",
    },
    // league_type: LeagueType?.name || "Redraft",
    league_type: "Redraft",
    players_away: updatedTradingAwayPlayers,
    players_for: updatedTradingForPlayers,
  };

  const fetchTradeAnalysis = async () => {
    // Prevent multiple API calls
    if (hasCalledApi.current) {
      return;
    }

    hasCalledApi.current = true;

    try {
      setIsLoading(true);
      setError(null);

      const response = await tradesAnalyze(tradesAnalyzeBody);

      if (response.status === 200) {
        setAnalysisResult(response.data);
        console.log("Trade Analysis Result:", response.data);
      } else {
        setError("Failed to analyze trade");
        console.error("Trade analysis failed:", response);
      }
    } catch (err) {
      setError("An error occurred while analyzing the trade");
      console.error("Trade analysis error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Call trades analyze API when component loads
  useEffect(() => {
    // Only call API if we have both trading away and trading for players
    if (
      updatedTradingAwayPlayers &&
      updatedTradingForPlayers &&
      updatedTradingAwayPlayers.length > 0 &&
      updatedTradingForPlayers.length > 0
    ) {
      fetchTradeAnalysis();
    } else {
      setIsLoading(false);
      setError("Missing player data for trade analysis");
    }
  }, [updatedTradingAwayPlayers, updatedTradingForPlayers]);

  const renderPlayerImages = (playersData: any, isTradeFor: boolean) => (
    <motion.div
      className={`flex space-x-[-10px] w-[40%] max-w-[40%] ${isTradeFor ? "" : "justify-end"}`}
      initial={{ opacity: 0, x: isTradeFor ? 20 : -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: 0.2 }}
    >
      {mapWithFallback(playersData).map((item, index) => (
        <motion.div
          key={item.id}
          className="w-[44px] h-[44px] rounded-full flex items-end overflow-hidden relative"
          style={{
            backgroundColor: item.playerTeamShort
              ? teamColors[item.playerTeamShort]
              : "#4F2683",
            zIndex: isTradeFor ? playersData.length - index : 1,
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.15, delay: 0.25 + index * 0.05 }}
        >
          <img
            className="scale-[1.7] rounded-full"
            src={item.playerHeadshotURL || NullPlayerImage}
            alt=""
          />
        </motion.div>
      ))}
    </motion.div>
  );

  const handleContinue = () => {
    Mixpanel.trackEvent("trade_analysis_closed", {
      name: "Trade Analysis Closed",
      analysisResult: analysisResult,
    });
    hapticsImpactLight();
    navigate("/chatbot-ai");
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-col h-screen justify-center items-center bg-black">
        <TradeAnalyzeLoading />

        <TextShimmer
          duration={2}
          className="text-[14px] mt-3 font-medium [--base-color:#448585] [--base-gradient-color:#6BCDD6] dark:[--base-color:#245C5B] dark:[--base-gradient-color:#4A9FA5]"
        >
          Analyzing Trade...
        </TextShimmer>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto pb-24">
        {error ? (
          // Show error in the main content area
          <div className="flex flex-col h-full justify-center items-center px-6">
            <p className="text-[#ff3333] text-[14px] font-medium text-center">
              {error}
            </p>
          </div>
        ) : (
          // Show normal trade analysis content
          <div className="relative">
            <div className="relative w-full max-w-[576px] mx-auto">
              <img
                src={tradeAnalyzeBg}
                alt={tradeAnalyzeBg}
                className="w-full h-full rounded-bl-[15px] rounded-br-[15px]"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <motion.div
                  className="flex items-center justify-center"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <img
                    src={ChatbotImg}
                    alt="AI Chat"
                    className="mr-2 w-[30.8px] h-[23.7px]"
                  />
                  <p className="text-white text-[22px] font-medium text-center">
                    RotoBot Says
                  </p>
                </motion.div>
                <motion.p
                  className="text-white text-[40px] font-bold text-center mt-3"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2, delay: 0.1 }}
                >
                  Take the Trade
                </motion.p>

                <div className="flex justify-center items-center gap-5 pt-4">
                  {renderPlayerImages(analysisResult?.players_away, false)}
                  <motion.div
                    className="text-white text-[14px] font-bold"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.15, delay: 0.15 }}
                  >
                    FOR
                  </motion.div>
                  {renderPlayerImages(analysisResult?.players_for, true)}
                </div>
              </div>
            </div>
            <div className="px-[20px]">
              <motion.div
                className="mt-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: 0.3 }}
              >
                <TextGenerateEffect
                  filter={false}
                  words={analysisResult?.analysis_text}
                />
                ;
              </motion.div>
            </div>
          </div>
        )}
      </div>

      {/* Fixed Continue button at bottom */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black via-black/90 to-transparent pt-4"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom) + 20px)",
        }}
      >
        <div className="px-[45px]">
          <motion.button
            className="h-[48px] bg-[#272B37] w-full flex justify-center items-center rounded-[50px]"
            onClick={handleContinue}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.35 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="text-white text-[12.03px] font-medium">
              Continue
            </span>
          </motion.button>
        </div>
      </div>
    </div>
  );
}
